import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { RouteObject } from 'react-router';
import { useSelector } from 'react-redux';

import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';
import SuspenseLoader from 'src/components/SuspenseLoader';
import { useRole } from 'src/contexts/RoleContext/RoleContext';
import { RootState } from 'src/store/store';

// ─── V2 Clinical Command Center ───────────────────────────────────────────────
const V2App = lazy(() => import('src/v2/V2App'));

const Loader = (Component: React.LazyExoticComponent<any>) => (props: any) =>
(
  <Suspense fallback={<SuspenseLoader />}>
    <Component {...props} />
  </Suspense>
);

// Lazy-loaded components
const Home = Loader(lazy(() => import('src/content/dashboards/Home')));
const SuperAdminHome = Loader(lazy(() => import('src/content/dashboards/SuperAdminHome')));
const DoctorHome = Loader(lazy(() => import('src/content/dashboards/DoctorHome')));
const NurseHome = Loader(lazy(() => import('src/content/dashboards/NurseHome')));

const PatientProfile = Loader(
  lazy(() => import('src/content/applications/UserProfile'))
);
const Transaction = Loader(
  lazy(() => import('src/content/applications/Transactions'))
);
const HelpCenter = Loader(
  lazy(() => import('src/content/applications/HelpCenter'))
);

const PatientQueriesPage = Loader(
  lazy(() => import('src/content/applications/PatientQueries/PatientQueriesPage'))
);

const PatientQueriesDetailPage = Loader(
  lazy(() => import('src/content/applications/Query/PatientQueriesWorkspace'))
);

const PatientReportsPage = Loader(
  lazy(() => import('src/content/applications/PatientReports/PatientReportsPage'))
);

const ViewSupportTickets = Loader(
  lazy(() => import('src/content/applications/HelpCenter/ViewSupportTickets'))
);
const SupportTicketQueue = Loader(
  lazy(() => import('src/content/applications/HelpCenter/SupportTicketQueue'))
);

const Patients = Loader(
  lazy(() => import('src/content/applications/SuperAdmin/AllPatients'))
);

const AssignedPatientsPage = Loader(
  lazy(() => import('src/content/applications/DoctorsPanel/AssignedPatientsPage'))
);

const UnassignedPatientsPage = Loader(
  lazy(() => import('src/content/applications/SuperAdmin/UnassignedPatients'))
);

const Doctors = Loader(
  lazy(() => import('src/content/applications/SuperAdmin/Doctors'))
);

const Nurses = Loader(
  lazy(() => import('src/content/applications/SuperAdmin/Nurses'))
);

const PendingQueries = Loader(
  lazy(() => import('src/content/applications/PendingQueries'))
);

const PendingReportsPage = Loader(
  lazy(() => import('src/content/applications/PendingReports/PendingReportsPage'))
);

const VideoConsultation = Loader(
  lazy(() => import('src/content/applications/VideoConsultation'))
);

const Services = Loader(
  lazy(() => import('src/content/applications/Services'))
);

const ImagingStudiesPage = Loader(
  lazy(() => import('src/content/applications/Viewer/ImagingStudiesPage'))
);

const ServiceDetails = Loader(lazy(() => import('src/content/applications/Services/ServiceDetails/ServiceDetails')));

const PrivacyPolicy = Loader(
  lazy(() => import('src/content/pages/PrivacyPolicy'))
);

const TermsAndConditions = Loader(
  lazy(() => import('src/content/pages/TermsAndConditions'))
);

// Role-Based Routes Wrapper
const RoleBasedRoutes = ({
  children,
  allowedRoles,
  requirePayment = false
}: {
  children: JSX.Element;
  allowedRoles: string[];
  requirePayment?: boolean;
}) => {
  const { role } = useRole();
  const hasPaidBefore = useSelector((state: RootState) => state.auth.hasPaidBefore);

  // Redirect if the user's role is not allowed
  if (!allowedRoles.includes(role)) {
    return <Navigate to={role === 'patient' ? '/' : '/patients'} replace />;
  }

  if (requirePayment && role === 'patient' && !hasPaidBefore) {
    return <Navigate to="/" replace />;
  }

  return children;
};


const PatientsRoleSwitch = () => {
  const { role } = useRole();

  if (role === 'admin') return <Patients />;
  if (role === 'nurse') return <Patients />;
  if (role === 'doctor') return <AssignedPatientsPage />;

  return <Navigate to="/" replace />;
};

const DashboardRoleSwitch = () => {
  const { role } = useRole();

  if (role === 'admin') return <SuperAdminHome />;
  if (role === 'doctor') return <DoctorHome />;
  if (role === 'nurse') return <NurseHome />;

  return <Home />;
};


const routes: RouteObject[] = [
  {
    path: 'v2/*',
    element: (
      <Suspense fallback={<SuspenseLoader />}>
        <V2App />
      </Suspense>
    )
  },

  // Public routes (accessible without authentication) - Direct routes without layout wrapper
  {
    path: 'privacy-policy',
    element: (
      <BaseLayout>
        <PrivacyPolicy />
      </BaseLayout>
    )
  },
  {
    path: 'terms-conditions',
    element: (
      <BaseLayout>
        <TermsAndConditions />
      </BaseLayout>
    )
  },
  // Authenticated routes
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <RoleBasedRoutes allowedRoles={['patient', 'admin', 'doctor', 'nurse']}>
            <DashboardRoleSwitch />
          </RoleBasedRoutes>
        )
      },
      {
        path: 'dashboard',
        element: <Navigate to="/" replace />
      },
      {
        path: 'patient-details',
        element: (
          <RoleBasedRoutes allowedRoles={['patient', 'doctor', 'admin', 'nurse']}>
            <PatientProfile />
          </RoleBasedRoutes>
        )
      },
      {
        path: 'patient-details/:sectionId',
        element: (
          <RoleBasedRoutes allowedRoles={['patient', 'admin']}>
            <PatientProfile />
          </RoleBasedRoutes>
        )
      },
      {
        path: 'transactions',
        element: (
          <RoleBasedRoutes allowedRoles={['patient', 'doctor', 'admin']}>
            <Transaction />
          </RoleBasedRoutes>
        )
      },
      {
        path: 'help',
        element: (
          <RoleBasedRoutes allowedRoles={['patient', 'doctor', 'nurse']}>
            <HelpCenter />
          </RoleBasedRoutes>
        )
      },

      {
        path: 'help/view-ticket',
        element: (
          <RoleBasedRoutes allowedRoles={['patient', 'doctor', 'nurse']}>
            <ViewSupportTickets />
          </RoleBasedRoutes>
        )
      },

      {
        // Staff-side queue over EVERY ticket. Deliberately excludes 'patient':
        // the endpoint returns other people's tickets and their email addresses.
        path: 'help/support-queue',
        element: (
          <RoleBasedRoutes allowedRoles={['admin', 'nurse']}>
            <SupportTicketQueue />
          </RoleBasedRoutes>
        )
      },

      {
        path: 'patient/reports',
        element: (
          <RoleBasedRoutes allowedRoles={['patient']}>
            <PatientReportsPage />
          </RoleBasedRoutes>
        )
      },

      {
        path: 'patient/queries',
        element: (
          <RoleBasedRoutes allowedRoles={['patient']}>
            <PatientQueriesPage />
          </RoleBasedRoutes>
        )
      },
      {
        path: 'patient/queries/:threadId',
        element: (
          <RoleBasedRoutes allowedRoles={['patient']}>
            <PatientQueriesPage />
          </RoleBasedRoutes>
        )
      },

      {
        path: 'patients',
        element: (
          <RoleBasedRoutes allowedRoles={['admin', 'doctor', 'nurse']}>
            <PatientsRoleSwitch />
          </RoleBasedRoutes>
        )
      },
      {
        path: 'patients/:patientId/queries',
        element: (
          <RoleBasedRoutes allowedRoles={['admin', 'doctor', 'nurse']}>
            <PatientQueriesDetailPage />
          </RoleBasedRoutes>
        )
      },
      {
        path: 'patients/:patientId/queries/:threadId',
        element: (
          <RoleBasedRoutes allowedRoles={['admin', 'doctor', 'nurse']}>
            <PatientQueriesDetailPage />
          </RoleBasedRoutes>
        )
      },
      {
        path: 'video-consultation',
        element: (
          <RoleBasedRoutes allowedRoles={['admin', 'doctor', 'nurse', 'patient']}>
            <VideoConsultation />
          </RoleBasedRoutes>
        )
      },
      {
        path: 'unassigned-patients',
        element: (
          <RoleBasedRoutes allowedRoles={['admin', 'nurse']}>
            <UnassignedPatientsPage />
          </RoleBasedRoutes>
        )
      },
      {
        path: 'doctors',
        element: (
          <RoleBasedRoutes allowedRoles={['admin', 'nurse']}>
            <Doctors />
          </RoleBasedRoutes>
        )
      },
      {
        path: 'nurses',
        element: (
          <RoleBasedRoutes allowedRoles={['admin', 'nurse']}>
            <Nurses />
          </RoleBasedRoutes>
        )
      },
      {
        path: 'queries',
        element: (
          <RoleBasedRoutes allowedRoles={['admin', 'nurse']}>
            <PendingQueries />
          </RoleBasedRoutes>
        )
      },
      {
        path: 'pending-reports',
        element: (
          <RoleBasedRoutes allowedRoles={['admin', 'nurse']}>
            <PendingReportsPage />
          </RoleBasedRoutes>
        )
      },
      {
        path: 'imaging-studies',
        element: (
          <RoleBasedRoutes allowedRoles={['doctor', 'admin', 'nurse']}>
            <ImagingStudiesPage />
          </RoleBasedRoutes>
        )
      },

      {
        path: 'services',
        element: (
          <RoleBasedRoutes allowedRoles={['patient']} requirePayment>
            <Services />
          </RoleBasedRoutes>
        )
      },

      {
        path: 'payment-success',
        element: <div />
      },

      {
        path: 'payment-failed',
        element: <div />
      }
    ]
  },

  {
    path: 'services/:serviceName',
    element: (
      <BaseLayout>
        <RoleBasedRoutes allowedRoles={['patient']}>
          <ServiceDetails />
        </RoleBasedRoutes>
      </BaseLayout>
    )
  }
];

export default routes;
